import React from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, CloudinaryImage, INGREDIENT_IMAGE_CONFIG, LabelText, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const PartnerLineItemIngredient = (props: Props) => {
  const { groupDisplayName, imageAlt, imageUrl, name, quantity, shouldUseCompactGrid } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const displayName =
    quantity > 1
      ? formatMessage(messages.nameWithPortion, { name, quantity: String(quantity) })
      : name;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = shouldUseCompactGrid ? undefined : styles.container;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <CloudinaryImage
        baseUrl={imageUrl}
        config={INGREDIENT_IMAGE_CONFIG}
        contentFit="cover"
        style={styles.image}
        alt={imageAlt}
      />

      <View style={styles.nameContainer}>
        <BodyText sizeMatch={['14']} style={styles.name}>
          {displayName}
        </BodyText>

        {groupDisplayName ? (
          <LabelText sizeMatch={['10']} style={styles.groupDisplayName}>
            {formatGroupDisplayName(groupDisplayName)}
          </LabelText>
        ) : null}
      </View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatGroupDisplayName(displayName: string) {
  const openingParenthesisIndex = displayName.indexOf('(');

  if (openingParenthesisIndex <= 0) {
    return displayName;
  }

  const name = displayName.slice(0, openingParenthesisIndex).trimEnd();
  const parenthetical = displayName.slice(openingParenthesisIndex);

  return `${name}\n${parenthetical}`;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_SIZE = 140;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: IMAGE_SIZE,
  },
  image: {
    width: '100%',
    aspectRatio: 1,
    borderWidth: 1,
    borderColor: theme.colors.BROWN,
    borderRadius: theme.spacing['4'],
  },
  nameContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['1'],
  },
  name: {
    color: theme.colors.BROWN,
    textAlign: 'center',
  },
  groupDisplayName: {
    color: theme.colors.CHARCOAL,
    textAlign: 'center',
    marginTop: theme.spacing['1'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly nameWithPortion: { readonly name: MessageValue; readonly quantity: string };
}>({
  nameWithPortion: {
    defaultMessage:
      '{name} ({quantity, select, 2 {double} 3 {triple} other {{quantity} portions}})',
    description: 'Partner line item | Ingredient | Name with portion label for multiple portions',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  imageAlt: string;
  imageUrl: string;
  groupDisplayName?: string;
  name: string;
  quantity: number;
  shouldUseCompactGrid: boolean;
};
